import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import { createFluidEvent, createClientFluidEvent, updateFluidEvent } from '../../services/calendar.service';
import { CalendarEventDto } from '@/types/calendar';
import { toastService } from '../../services/toast.service';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';
import { toLocalIso } from '@/utils/datetime';
import { SchedulingBlock } from '../calendar/add-event-modals/SchedulingBlock';
import { eventColors } from '@/styles/tokens';

const ACCENT      = eventColors.water.main;
const ACCENT_HOVER = eventColors.water.dark;

interface QuickWaterAddModalProps {
  open: boolean;
  onClose: () => void;
  selectedDate: string;
  initialScheduledAt?: Date;
  onRefreshDashboard?: () => void;
  clientId?: string;
  eventToEdit?: CalendarEventDto | null;
}

const GLASSES = [
  { ml: 150, w: 28, h: 34 },
  { ml: 200, w: 32, h: 38 },
  { ml: 250, w: 36, h: 44 },
  { ml: 300, w: 40, h: 50 },
  { ml: 500, w: 46, h: 58 },
];

const GlassSvg: React.FC<{ w: number; h: number; filled: boolean; gradientId: string }> = ({ w, h, filled, gradientId }) => {
  const pad = 3;
  const topW = w - pad;
  const botW = w * 0.6;
  const cx = w / 2;
  const x1 = cx - topW / 2;
  const x2 = cx + topW / 2;
  const x3 = cx + botW / 2;
  const x4 = cx - botW / 2;

  return (
    <svg width={w + 8} height={h + 6} viewBox={`0 0 ${w + 8} ${h + 6}`}>
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#AFF5FF" />
          <stop offset="100%" stopColor="#2CBBFF" />
        </linearGradient>
      </defs>
      <polygon
        points={`${x1 + 4},${pad} ${x2 + 4},${pad} ${x3 + 4},${h + pad} ${x4 + 4},${h + pad}`}
        fill={filled ? `url(#${gradientId})` : '#E3F2FD'}
        stroke={filled ? '#0288d1' : '#B0C4DE'}
        strokeWidth="1.5"
      />
      {filled && (
        <line
          x1={x1 + 4} y1={pad + 6}
          x2={x2 + 4} y2={pad + 6}
          stroke="rgba(255,255,255,0.6)"
          strokeWidth="2"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
};

export const QuickWaterAddModal: React.FC<QuickWaterAddModalProps> = ({
  open,
  onClose,
  selectedDate,
  initialScheduledAt,
  onRefreshDashboard,
  clientId,
  eventToEdit,
}) => {
  const isEditMode = !!eventToEdit;
  const [value, setValue] = useState<string>('');
  const [selectedMl, setSelectedMl] = useState<number | null>(null);
  const [showDateTimePicker, setShowDateTimePicker] = useState(!!initialScheduledAt);
  const [scheduledAt, setScheduledAt] = useState<Date | null>(initialScheduledAt || null);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (eventToEdit) {
      const qty = (eventToEdit.details as any)?.qty;
      setValue(qty ? String(qty) : '');
      setSelectedMl(GLASSES.find(g => g.ml === qty)?.ml ?? null);
      if (eventToEdit.scheduledLdt) {
        setScheduledAt(new Date(eventToEdit.scheduledLdt));
        setShowDateTimePicker(true);
      }
      setIsCompleted(eventToEdit.isCompleted ?? false);
    } else {
      setValue('');
      setSelectedMl(null);
      setShowDateTimePicker(!!initialScheduledAt);
      setScheduledAt(initialScheduledAt || null);
      setIsCompleted(false);
    }
  }, [open, eventToEdit]);

  const handleGlassClick = (ml: number) => {
    setSelectedMl(ml);
    setValue(String(ml));
  };

  const handleConfirm = async () => {
    const ml = parseInt(value, 10);
    if (!ml || ml <= 0) return;
    try {
      const now = new Date();
      const scheduledLdtFinal = showDateTimePicker && scheduledAt
        ? toLocalIso(scheduledAt)
        : toLocalIso(new Date(`${selectedDate}T${now.toTimeString().slice(0, 8)}`));
      const finalIsCompleted = showDateTimePicker ? isCompleted : true;

      if (isEditMode && eventToEdit) {
        await updateFluidEvent(eventToEdit.id, { qty: ml, scheduledLdt: scheduledLdtFinal, isCompleted: finalIsCompleted });
        toastService.success('Воду успішно оновлено');
      } else if (clientId) {
        await createClientFluidEvent(clientId, { qty: ml, scheduledLdt: scheduledLdtFinal, isCompleted: finalIsCompleted });
        toastService.success('Воду успішно додано');
      } else {
        await createFluidEvent({ qty: ml, scheduledLdt: scheduledLdtFinal, isCompleted: finalIsCompleted });
        toastService.success('Воду успішно додано');
      }

      if (onRefreshDashboard) onRefreshDashboard();
      onClose();
    } catch (error) {
      console.error('Fluid save error:', error);
      toastService.error('Не вдалося зберегти. Спробуйте ще раз.');
    }
  };

  const handleClose = () => {
    setValue('');
    setSelectedMl(null);
    setShowDateTimePicker(false);
    setScheduledAt(null);
    setIsCompleted(false);
    onClose();
  };

  const numericValue = parseInt(value, 10);
  const canConfirm = !isNaN(numericValue) && numericValue > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '20px' } }}
      data-i-doc={ModalIndices.QUICK_WATER_ADD}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalDrinkIcon sx={{ color: ACCENT }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{isEditMode ? 'Редагувати воду' : 'Додати воду'}</Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 2, pt: 1 }}>
        {/* Стакани */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          gap: { xs: 1.5, sm: 2 },
          mb: 3,
        }}>
          {GLASSES.map(({ ml, w, h }) => {
            const isSel = selectedMl === ml;
            const gradientId = `wg-${ml}`;
            return (
              <Box
                key={ml}
                onClick={() => handleGlassClick(ml)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  transform: isSel ? 'scale(1.12) translateY(-3px)' : 'scale(1)',
                  transition: 'transform 0.18s ease',
                  '&:hover': {
                    transform: 'scale(1.08) translateY(-2px)',
                    '& .gl': { color: '#0288d1' },
                  },
                }}
              >
                <GlassSvg w={w} h={h} filled={isSel} gradientId={gradientId} />
                <Typography
                  className="gl"
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: isSel ? ACCENT : 'text.secondary',
                    fontSize: '0.63rem',
                    transition: 'color 0.15s',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {ml} мл
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Поле ручного введення */}
        <TextField
          fullWidth
          type="number"
          label="Об'єм"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setSelectedMl(null);
          }}
          onKeyDown={(e) => { if (e.key === 'Enter' && canConfirm) handleConfirm(); }}
          InputProps={{
            endAdornment: <InputAdornment position="end">мл</InputAdornment>,
          }}
          inputProps={{ min: 1, step: 50 }}
          sx={{ bgcolor: 'background.paper', '& .MuiInputBase-root': { borderRadius: '12px' } }}
        />

        <SchedulingBlock
          showDateTimePicker={showDateTimePicker}
          onShowDateTimePickerChange={setShowDateTimePicker}
          scheduledAt={scheduledAt}
          onScheduledAtChange={setScheduledAt}
          isCompleted={isCompleted}
          onIsCompletedChange={setIsCompleted}
          switchLabel="Встановити дату та час"
          switchSize="small"
        />
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button onClick={handleClose} color="inherit" sx={{ flex: 1 }}>
          Скасувати
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!canConfirm}
          sx={{ flex: 1, bgcolor: ACCENT, borderRadius: '10px', '&:hover': { bgcolor: ACCENT_HOVER } }}
        >
          {isEditMode ? 'Зберегти' : 'Додати'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
